// pkg79a — HQ operator wizard (D-79a2): at hq.lawmighty.com (firm.is_hq) the setup collects ONLY the operator login — name, email, password — with LawMighty-HQ copy; no firm section, no address, no bar number (HQ is the LLC's portal for managing the platform, not a law firm); tenant-firm setup unchanged — delivered 2026-07-13
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { publicApi } from '../utils/api';
import { useFirmSettings } from '../contexts/FirmSettingsContext';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

// First-run setup wizard. Renders only when no owner exists in admin_users.
// Once an owner is created, the backend rejects further requests and this
// page stops being routed to.
export default function AdminSetupPage({ onComplete, alreadyDone }) {
  const navigate = useNavigate();
  const { firm, refresh } = useFirmSettings(); // firm pkg79a — is_hq selects the operator wizard
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    firm_name: '',
    phone: '',
    client_contact_email: '',
    business_street: '',
    business_city: '',
    business_state: 'TN',
    business_zip: '',
    owner_name: '',
    owner_email: '',   // pkg79a — HQ variant defaults this to mail@lawmighty.com below
    owner_username: '',
    owner_password: '',
    owner_password_confirm: '',
    owner_bar_number: '',
  });

  // If setup is already done, this page is unreachable.
  if (alreadyDone) return <Navigate to="/admin" replace />;

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  // pkg79a (D-79a2) — the HQ operator wizard. hq.lawmighty.com is the LLC's
  // portal for managing the platform — not a law firm — so setup collects
  // ONLY the operator login. The backend accepts the trimmed payload when
  // the requesting firm is the HQ row.
  if (firm?.is_hq) {
    const email = form.owner_email || 'mail@lawmighty.com';
    const submitHq = async (e) => {
      e.preventDefault();
      setError('');
      if (form.owner_password.length < 8) return setError('Password must be at least 8 characters.');
      if (form.owner_password !== form.owner_password_confirm) return setError('Password and confirmation do not match.');
      setSubmitting(true);
      try {
        await publicApi.initializeSetup({
          owner_name: form.owner_name,
          owner_email: email,
          owner_password: form.owner_password,
        });
        await refresh();
        if (onComplete) onComplete();
        navigate('/admin');
      } catch (err) { setError(err.message); }
      finally { setSubmitting(false); }
    };
    return (
      <div style={{ minHeight: '100vh', background: '#F5F1E8', padding: '40px 16px' }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, marginBottom: 6 }}>Set up LawMighty HQ.</h1>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, color: '#555', marginBottom: 28 }}>
            This is the operator portal for managing LawMighty — the firms, trials, and subscriptions
            on the platform. It is not a law firm account. Create the operator login to continue.
          </p>
          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
          <form onSubmit={submitHq}>
            <div className="card">
              <div className="form-group">
                <label>Your Name <span style={req}>*</span></label>
                <input type="text" required value={form.owner_name}
                  onChange={e => set('owner_name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Login Email <span style={req}>*</span></label>
                <input type="email" required value={email}
                  onChange={e => set('owner_email', e.target.value)} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Password <span style={req}>*</span></label>
                  <input type="password" required value={form.owner_password}
                    onChange={e => set('owner_password', e.target.value)}
                    placeholder="At least 8 characters" />
                </div>
                <div className="form-group">
                  <label>Confirm Password <span style={req}>*</span></label>
                  <input type="password" required value={form.owner_password_confirm}
                    onChange={e => set('owner_password_confirm', e.target.value)} />
                </div>
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={submitting} style={{ marginTop: 8 }}>
              {submitting ? <><span className="spinner" />Setting up...</> : 'Create Operator Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (form.owner_password.length < 8) {
      return setError('Owner password must be at least 8 characters.');
    }
    if (form.owner_password !== form.owner_password_confirm) {
      return setError('Owner password and confirmation do not match.');
    }

    setSubmitting(true);
    try {
      const payload = { ...form };
      delete payload.owner_password_confirm;
      await publicApi.initializeSetup(payload);
      await refresh();
      if (onComplete) onComplete();
      navigate('/admin');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F5F1E8', padding: '40px 16px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, marginBottom: 6 }}>Welcome.</h1>
        <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, color: '#555', marginBottom: 28 }}>
          Set up your firm and the first owner account. The owner has full access and cannot be deleted unless another owner is promoted first. Bar number is required so generated filings include it on the signature block.
        </p>

        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="section-head" style={{ marginTop: 0 }}>Firm Information</div>
          <div className="card">
            <div className="form-group">
              <label>Firm Name <span style={req}>*</span></label>
              <input type="text" required value={form.firm_name}
                onChange={e => set('firm_name', e.target.value)} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" value={form.phone}
                  onChange={e => set('phone', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Contact Email</label>
                <input type="email" value={form.client_contact_email}
                  onChange={e => set('client_contact_email', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="section-head">Business Address</div>
          <div className="card">
            <div className="form-group">
              <label>Street</label>
              <input type="text" value={form.business_street}
                onChange={e => set('business_street', e.target.value)} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input type="text" value={form.business_city}
                  onChange={e => set('business_city', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: '0 0 100px' }}>
                <label>State</label>
                <select value={form.business_state}
                  onChange={e => set('business_state', e.target.value)}>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: '0 0 120px' }}>
                <label>ZIP</label>
                <input type="text" value={form.business_zip}
                  onChange={e => set('business_zip', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="section-head">First Owner</div>
          <div className="card">
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 0, marginBottom: 14 }}>
              At least one owner is required. Bar number appears on every filing this owner signs.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name <span style={req}>*</span></label>
                <input type="text" required value={form.owner_name}
                  onChange={e => set('owner_name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Bar Number <span style={req}>*</span></label>
                <input type="text" required value={form.owner_bar_number}
                  onChange={e => set('owner_bar_number', e.target.value)}
                  placeholder="e.g. BPR # 024983 or State Bar No. 123456" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email <span style={req}>*</span></label>
                <input type="email" required value={form.owner_email}
                  onChange={e => set('owner_email', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Username <span style={req}>*</span></label>
                <input type="text" required value={form.owner_username}
                  onChange={e => set('owner_username', e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                  placeholder="lowercase, no spaces" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Password <span style={req}>*</span></label>
                <input type="password" required value={form.owner_password}
                  onChange={e => set('owner_password', e.target.value)}
                  placeholder="At least 8 characters" />
              </div>
              <div className="form-group">
                <label>Confirm Password <span style={req}>*</span></label>
                <input type="password" required value={form.owner_password_confirm}
                  onChange={e => set('owner_password_confirm', e.target.value)} />
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={submitting} style={{ marginTop: 8 }}>
            {submitting ? <><span className="spinner" />Setting up...</> : 'Complete Setup'}
          </button>
        </form>
      </div>
    </div>
  );
}

const req = { color: '#B85042', marginLeft: 2 };
